/**
 * Configuration validation utilities for the logging system
 * 
 * Provides validation and default value handling for LoggingConfig options
 */

import { LoggingConfig, DEFAULT_LOGGING_CONFIG } from '../models/logging-config.interface';

/**
 * Validates and normalizes a LoggingConfig object
 * 
 * @param config - Partial logging configuration to validate
 * @returns Validated configuration with defaults applied
 */
export function validateLoggingConfig(config: Partial<LoggingConfig> = {}): LoggingConfig {
  const validatedConfig: LoggingConfig = {};

  // Apply defaults for all standard configuration options
  validatedConfig.batchSize = validatePositiveNumber(config.batchSize, DEFAULT_LOGGING_CONFIG.batchSize, 'batchSize');
  validatedConfig.flushIntervalMs = validatePositiveNumber(config.flushIntervalMs, DEFAULT_LOGGING_CONFIG.flushIntervalMs, 'flushIntervalMs');
  validatedConfig.maxRetries = validatePositiveNumber(config.maxRetries, DEFAULT_LOGGING_CONFIG.maxRetries, 'maxRetries');
  validatedConfig.maxQueueSize = validatePositiveNumber(config.maxQueueSize, DEFAULT_LOGGING_CONFIG.maxQueueSize, 'maxQueueSize');
  validatedConfig.metricsReportingIntervalMs = validatePositiveNumber(config.metricsReportingIntervalMs, DEFAULT_LOGGING_CONFIG.metricsReportingIntervalMs, 'metricsReportingIntervalMs');
  validatedConfig.apiTimeoutMs = validatePositiveNumber(config.apiTimeoutMs, DEFAULT_LOGGING_CONFIG.apiTimeoutMs, 'apiTimeoutMs');

  // Apply defaults for new authentication failure queue options
  validatedConfig.authFailureQueueSize = validatePositiveNumber(config.authFailureQueueSize, DEFAULT_LOGGING_CONFIG.authFailureQueueSize, 'authFailureQueueSize');
  validatedConfig.authRetryIntervalMs = validatePositiveNumber(config.authRetryIntervalMs, DEFAULT_LOGGING_CONFIG.authRetryIntervalMs, 'authRetryIntervalMs');

  // Boolean options with defaults
  validatedConfig.enableConsoleOutput = config.enableConsoleOutput ?? DEFAULT_LOGGING_CONFIG.enableConsoleOutput;
  validatedConfig.enableCloudWatchOnStartup = config.enableCloudWatchOnStartup ?? DEFAULT_LOGGING_CONFIG.enableCloudWatchOnStartup;
  validatedConfig.enableDebugLogging = config.enableDebugLogging ?? DEFAULT_LOGGING_CONFIG.enableDebugLogging;
  validatedConfig.enableMetricsReporting = config.enableMetricsReporting ?? DEFAULT_LOGGING_CONFIG.enableMetricsReporting;
  validatedConfig.suppressAuthErrorsOnLogout = config.suppressAuthErrorsOnLogout ?? DEFAULT_LOGGING_CONFIG.suppressAuthErrorsOnLogout;

  // String options with defaults
  validatedConfig.logGroup = config.logGroup || DEFAULT_LOGGING_CONFIG.logGroup;

  // Array options with defaults
  validatedConfig.sensitiveFieldPatterns = config.sensitiveFieldPatterns || DEFAULT_LOGGING_CONFIG.sensitiveFieldPatterns;

  return validatedConfig;
}

/**
 * Validates that a number is positive and returns the default if invalid
 * 
 * @param value - Value to validate
 * @param defaultValue - Default value to use if validation fails
 * @param fieldName - Name of the field for error logging
 * @returns Validated positive number
 */
function validatePositiveNumber(value: number | undefined, defaultValue: number, fieldName: string): number {
  if (typeof value === 'number' && value > 0 && !isNaN(value)) {
    return value;
  }

  if (value !== undefined) {
    console.warn(`[LoggingConfig] Invalid ${fieldName}: ${value}. Using default: ${defaultValue}`);
  }

  return defaultValue;
}

/**
 * Validates authentication failure queue specific configuration
 * 
 * @param config - Configuration to validate
 * @returns Validation result with any issues found
 */
export function validateAuthFailureQueueConfig(config: LoggingConfig): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];

  // Validate authFailureQueueSize
  if (config.authFailureQueueSize !== undefined) {
    if (config.authFailureQueueSize < 1) {
      issues.push('authFailureQueueSize must be at least 1');
    } else if (config.authFailureQueueSize > 1000) {
      issues.push('authFailureQueueSize should not exceed 1000 to prevent memory issues');
    }
  }

  // Validate authRetryIntervalMs
  if (config.authRetryIntervalMs !== undefined) {
    if (config.authRetryIntervalMs < 1000) {
      issues.push('authRetryIntervalMs should be at least 1000ms to prevent excessive API calls');
    } else if (config.authRetryIntervalMs > 300000) {
      issues.push('authRetryIntervalMs should not exceed 300000ms (5 minutes) for reasonable retry frequency');
    }
  }

  return {
    isValid: issues.length === 0,
    issues
  };
}

/**
 * Gets the effective configuration value with environment-specific defaults
 * 
 * @param config - User-provided configuration
 * @param environment - Current environment ('dev', 'test', 'prod')
 * @returns Configuration with environment-appropriate defaults
 */
export function getEffectiveConfig(config: Partial<LoggingConfig>, environment: string): LoggingConfig {
  const baseConfig = validateLoggingConfig(config);

  // Apply environment-specific optimizations
  switch (environment) {
    case 'prod':
    case 'production':
      return {
        ...baseConfig,
        // Production optimizations: smaller queue, longer retry interval
        authFailureQueueSize: config.authFailureQueueSize ?? 50,
        authRetryIntervalMs: config.authRetryIntervalMs ?? 60000,
        enableConsoleOutput: config.enableConsoleOutput ?? false,
        enableDebugLogging: config.enableDebugLogging ?? false,
        enableMetricsReporting: config.enableMetricsReporting ?? false
      };

    case 'test':
    case 'staging':
      return {
        ...baseConfig,
        // Test environment: standard settings, no console output
        enableConsoleOutput: config.enableConsoleOutput ?? false,
        enableDebugLogging: config.enableDebugLogging ?? false,
        enableMetricsReporting: config.enableMetricsReporting ?? false
      };

    case 'dev':
    case 'development':
    default:
      return {
        ...baseConfig,
        // Development: enable debugging features
        enableConsoleOutput: config.enableConsoleOutput ?? true,
        enableDebugLogging: config.enableDebugLogging ?? true,
        enableMetricsReporting: config.enableMetricsReporting ?? true
      };
  }
}